import { DEVICE_DONUT_COLORS, UNKNOWN_CT_BLUE } from '~styles/colors';
import { enumerateDates } from '~utils/enumerate-dates';
import type { KpiCardData } from '../types';

type DownloadsData = Extract<KpiCardData, { form: 'downloads' }>;

/** One platform's named daily series, as the source composed it. */
export type DownloadsSeries = DownloadsData['series'][number];

/**
 * One x-axis day. Besides `date`, the row carries one cell per series, keyed by
 * that series' label, holding the day's value or `null` when the series has no
 * data for it.
 */
export interface DownloadsChartRow {
    date: string;
    [seriesLabel: string]: string | number | null;
}

/**
 * Spread the named series onto one row per day of the axis.
 *
 * The axis is the card's `range`, not the union of the series' dates: it spans
 * the declared window so a series that stops early visibly stops early instead
 * of quietly redefining where the chart ends.
 *
 * It renders `range` whole or not at all. The composition guarantees `range` is
 * a window `enumerateDates` can produce in full (it shortens anything longer,
 * and says so in the card's reason), so the cap is never reached from here. If
 * it somehow were, cutting the axis short would put both lines' last points on
 * the same edge and invent a shared staleness date, so this returns nothing at
 * all rather than draw a chart that lies.
 *
 * Days a series did not report get `null`, never 0. That is the whole point of
 * this form: recharts breaks a line on a `null` (with `connectNulls` off) and
 * draws no dot, so a series that has run out of data ENDS. A day the platform
 * did report as 0 is a real zero and is plotted as one, which is why gaps are
 * left as gaps and never filled.
 */
export const buildDownloadsChartRows = (
    range: DownloadsData['range'],
    series: DownloadsSeries[]
): DownloadsChartRow[] => {
    const days = enumerateDates(range.start, range.end);
    // The enumeration stopped at its cap, so these days are not the range the
    // card declared. Refuse rather than mislead (see above).
    if (days.length > 0 && days[days.length - 1] !== range.end) {
        return [];
    }
    const byLabel = series.map((entry) => ({
        label: entry.label,
        values: new Map(entry.points.map((point) => [point.date, point.value])),
    }));

    return days.map((date) => {
        const row: DownloadsChartRow = { date };
        for (const entry of byLabel) {
            row[entry.label] = entry.values.get(date) ?? null;
        }
        return row;
    });
};

/** Lower-cased lookup, so label casing drift still maps to the brand color. */
const SERIES_COLORS = new Map(
    Object.entries(DEVICE_DONUT_COLORS).map(([label, color]) => [
        label.toLowerCase(),
        color,
    ])
);

/**
 * Line color for a named series. iOS and Android reuse the by-device donut's
 * brand colors, so the same platform reads the same color on both cards; any
 * other label falls back to the default chart blue.
 */
export const downloadsSeriesColor = (label: string): string =>
    SERIES_COLORS.get(label.toLowerCase()) ?? UNKNOWN_CT_BLUE;

/** `2026-06-15` -> `06-15`: the axis is one period, so the year is noise. */
export const formatDownloadsAxisDate = (date: string): string => date.slice(5);
